import { useState } from 'react';
import { SheetModal } from './SheetModal';
import { isDefined } from '@shared';
import { cn } from '@client/lib';

interface SlideOutSheetProps {
  slideOutContent?: JSX.Element;
  maxWidth?: string;
  title: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  noMargin?: boolean;
}

export function SlideOutSheet(
  props: React.PropsWithChildren<SlideOutSheetProps>,
) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  const isOpen = isDefined(props.open) ? props.open : internalOpen;
  const handleClose = () => {
    if (props.setOpen) {
      props.setOpen(false);
    } else {
      setInternalOpen(false);
    }
  };
  
  const handleOpen = () => {
    if (props.setOpen) {
      props.setOpen(true);
    } else {
      setInternalOpen(true);
    }
  };
  
  // Convert maxWidth prop to className for SheetModal
  const getWidthClassName = () => {
    if (!props.maxWidth) return 'sm:min-w-[85%] md:min-w-[600px] lg:min-w-[700px]'; // Default wider width

    // Convert Tailwind max-width classes to min-width classes (made wider)
    const maxWidthMap: Record<string, string> = {
      'max-w-sm': 'sm:min-w-[80%] md:min-w-[500px]',
      'max-w-md': 'sm:min-w-[85%] md:min-w-[600px] lg:min-w-[700px]',
      'max-w-lg': 'sm:min-w-[90%] md:min-w-[700px] lg:min-w-[800px]',
      'max-w-xl': 'sm:min-w-[92%] md:min-w-[800px] lg:min-w-[900px]',
      'max-w-2xl': 'sm:min-w-[95%] md:min-w-[900px] lg:min-w-[1000px]',
      'max-w-5xl': 'sm:min-w-[96%] md:min-w-[95%] lg:min-w-[1700px]',
      'custom-roi': 'min-w-[95%] sm:min-w-[90%] md:min-w-[600px] lg:min-w-[650px]',
    };

    return maxWidthMap[props.maxWidth] || 'sm:min-w-[85%] md:min-w-[600px] lg:min-w-[700px]';
  };

  // Check if this is ROI content that needs special spacing
  const isROIContent = props.slideOutContent?.type?.name === 'ROISlideOutContent';

  return (
    <>
      {/* Button to open slide-out */}
      <button
        type="button"
        onClick={handleOpen}
      >
        {props.children}
        <span className="sr-only">Learn more</span>
      </button>
      
      <SheetModal
        open={isOpen}
        onClose={handleClose}
        title={`Waterlily ${props.title}`}
        subTitle=""
        className={getWidthClassName()}
      >
        {isROIContent ? (
          <div className="flex min-h-full flex-1 flex-col overflow-auto text-black">
            <div className="flex flex-1 flex-col py-4">
              {props.slideOutContent}
            </div>
          </div>
        ) : (
          <div
            className={cn('mt-[6vh] flex flex-col justify-between gap-2 text-gray-800', {
              'mt-0': props.noMargin,
            })}
          >
            {props.slideOutContent}
          </div>
        )}
      </SheetModal>
    </>
  );
}
